import React, {
  memo,
  type ReactElement,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  type Active,
  DndContext,
  type DragEndEvent,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  type UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Box,
  Button,
  Checkbox,
  FooterActionBar,
  Icon,
  InlineMessage,
  ModalIsland,
} from '@saviynt/design-system';

import { useInvertedLocalize } from '../../../../../shared/hooks';

import './GraphicalViewWidgetMenu.css';

const MAX_WIDGETS = 6;
const BASE_ITEM_HEIGHT = 74;

/**
 * Utility to move an item in an array from one index to another
 */
function moveArrayItem<T>(array: T[], fromIndex: number, toIndex: number): T[] {
  const newArray = [...array];
  const [moved] = newArray.splice(fromIndex, 1);

  newArray.splice(toIndex, 0, moved);

  return newArray;
}

interface GraphicalViewWidgetMenuProps {
  isMenuOpen: boolean;
  setIsMenuOpen: (state: boolean) => void;
  widgets: {
    id: string;
    label: string;
    isChecked: boolean;
  }[];
  onWidgetsChange: (
    widgets: { id: string; label: string; isChecked: boolean }[]
  ) => void;
}

interface DraggableWidgetItemProps {
  widget: { id: string; label: string; isChecked: boolean };
  isDisabled: boolean;
  onToggle: (id: string) => void;
}

/**
 * Draggable widget item component
 */
function DraggableWidgetItem({
  widget,
  isDisabled,
  onToggle,
}: DraggableWidgetItemProps): ReactElement {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: widget.id as UniqueIdentifier });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? 'grab' : 'default',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className='GraphicalView-WidgetMenu-item'
      // eslint-disable-next-line react/jsx-props-no-spreading -- Required to use DND attributes
      {...attributes}
      // eslint-disable-next-line react/jsx-props-no-spreading -- Required to use DND listeners
      {...listeners}
      role='button'
      tabIndex={0}>
      <div className='GraphicalView-WidgetMenu-dragHandle'>
        <Icon kind='DragVertical' size='small' />
      </div>
      <div className='GraphicalView-WidgetMenu-card'>
        <div className='GraphicalView-WidgetMenu-cardContent'>
          <p className='GraphicalView-WidgetMenu-cardLabel'>{widget.label}</p>
        </div>
        <Checkbox
          isChecked={widget.isChecked}
          onChange={() => onToggle(widget.id)}
          isDisabled={!widget.isChecked && isDisabled}
        />
      </div>
    </div>
  );
}

/**
 * @description Modal for customizing which widgets are displayed in the Graphical View.
 * Supports drag-and-drop reordering and toggling visibility of widgets up to a maximum limit.
 * @param {GraphicalViewWidgetMenuProps} props - Component props
 * @param {boolean} props.isMenuOpen - Whether the modal is open
 * @param {(state: boolean) => void} props.setIsMenuOpen - Callback to open or close the modal
 * @param {{ id: string; label: string; isChecked: boolean }[]} props.widgets - Array of widget configuration objects
 * @param {(widgets: { id: string; label: string; isChecked: boolean }[]) => void} props.onWidgetsChange - Callback invoked with the updated widget list after reorder or toggle
 * @returns {React.ReactElement} The widget menu modal
 */
function GraphicalViewWidgetMenu({
  isMenuOpen,
  setIsMenuOpen,
  widgets,
  onWidgetsChange,
}: GraphicalViewWidgetMenuProps): ReactElement {
  const l = useInvertedLocalize();
  const dropDownBodyRef = useRef<HTMLDivElement | null>(null);
  const [draggedItem, setDraggedItem] = useState<UniqueIdentifier | null>(null);
  const [draggingItemIndex, setDraggingItemIndex] = useState<number | null>(
    null
  );
  const [draggingItemScrollAmount, setDraggingItemScrollAmount] =
    useState<number>(0);

  const activeWidgetCount = useMemo(
    () => widgets.filter((widget) => widget.isChecked).length,
    [widgets]
  );

  const isWidgetOverflow = useMemo(
    () => activeWidgetCount >= MAX_WIDGETS,
    [activeWidgetCount]
  );

  const checkedWidgets = useMemo(
    () => widgets.filter((w) => w.isChecked),
    [widgets]
  );

  const uncheckedWidgets = useMemo(
    () => widgets.filter((w) => !w.isChecked),
    [widgets]
  );

  const handleWidgetToggle = useCallback(
    (widgetId: string) => {
      const updatedWidgets = widgets.map((widget) => {
        if (widget.id === widgetId) {
          // If unchecking, always allow
          // If checking, only allow if not at max
          if (!widget.isChecked && isWidgetOverflow) {
            return widget;
          }

          return { ...widget, isChecked: !widget.isChecked };
        }

        return widget;
      });

      onWidgetsChange(updatedWidgets);
    },
    [widgets, isWidgetOverflow, onWidgetsChange]
  );

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 20,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = ({ active }: { active: Active }) => {
    const scrollAmount = dropDownBodyRef.current?.scrollTop ?? 0;
    const index = checkedWidgets.findIndex((w) => w.id === active.id);

    setDraggedItem(active.id);
    setDraggingItemIndex(index);
    setDraggingItemScrollAmount(scrollAmount);
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    setDraggedItem(null);
    setDraggingItemIndex(null);
    setDraggingItemScrollAmount(0);

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = checkedWidgets.findIndex((w) => w.id === active.id);
    const newIndex = checkedWidgets.findIndex((w) => w.id === over.id);

    if (oldIndex < 0 || newIndex < 0) {
      return;
    }

    // Use moveArrayItem to reorder checked widgets
    const newCheckedWidgets = moveArrayItem(checkedWidgets, oldIndex, newIndex);

    // Combine checked widgets (in new order) with unchecked widgets
    const reorderedWidgets = [...newCheckedWidgets, ...uncheckedWidgets];

    // Update the parent component with the new order
    onWidgetsChange(reorderedWidgets);
  };

  const draggedWidget = useMemo(
    () =>
      draggedItem ? checkedWidgets.find((w) => w.id === draggedItem) : null,
    [checkedWidgets, draggedItem]
  );

  const overlayTopItemPosition =
    draggingItemIndex !== null
      ? draggingItemIndex * BASE_ITEM_HEIGHT - draggingItemScrollAmount
      : 0;

  const overlayStyle: React.CSSProperties = {
    position: 'absolute',
    top: overlayTopItemPosition,
    left: 0,
    opacity: 1,
  };

  const handleSave = useCallback(() => {
    setIsMenuOpen(false);
  }, [setIsMenuOpen]);

  const handleCancel = useCallback(() => {
    setIsMenuOpen(false);
  }, [setIsMenuOpen]);

  return (
    <Box className='GraphicalView-WidgetMenu-wrapper'>
      <ModalIsland
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        isOutsideClickEnabled
        title={l('Add/Remove Widgets ')}
        subtitle={l(
          'Select up to {count} widgets to be displayed and drag to reorder them.',
          { count: MAX_WIDGETS }
        )}
        headerIcon={
          <Icon kind='Settings' size='large' className='widgets-icon' />
        }
        className='GraphicalView-WidgetMenu-modal'
        classNames={{
          bodyClassName: 'GraphicalView-WidgetMenu-contentWrapper',
        }}
        contentBody={
          <Box className='GraphicalView-WidgetMenu-content'>
            <div style={{ position: 'relative' }}>
              <DndContext
                sensors={sensors}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}>
                <div
                  ref={dropDownBodyRef}
                  className='GraphicalView-WidgetMenu-list'>
                  <SortableContext
                    items={checkedWidgets.map((w) => w.id)}
                    strategy={verticalListSortingStrategy}>
                    {checkedWidgets.map((widget) => (
                      <DraggableWidgetItem
                        key={widget.id}
                        widget={widget}
                        isDisabled={isWidgetOverflow}
                        onToggle={handleWidgetToggle}
                      />
                    ))}
                  </SortableContext>
                  <div className='GraphicalView-WidgetMenu-uncheckedList'>
                    {uncheckedWidgets.map((widget) => (
                      <div
                        key={widget.id}
                        className='GraphicalView-WidgetMenu-item'>
                        <div className='GraphicalView-WidgetMenu-dragHandle' />
                        <div className='GraphicalView-WidgetMenu-card'>
                          <div className='GraphicalView-WidgetMenu-cardContent'>
                            <p className='GraphicalView-WidgetMenu-cardLabel'>
                              {widget.label}
                            </p>
                          </div>
                          <Checkbox
                            isChecked={widget.isChecked}
                            onChange={() => handleWidgetToggle(widget.id)}
                            isDisabled={isWidgetOverflow}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <DragOverlay style={overlayStyle}>
                  {draggedWidget ? (
                    <div className='GraphicalView-WidgetMenu-dragOverlay'>
                      <div className='GraphicalView-WidgetMenu-item'>
                        <div className='GraphicalView-WidgetMenu-dragHandle'>
                          <Icon kind='DragVertical' size='small' />
                        </div>
                        <div className='GraphicalView-WidgetMenu-card'>
                          <div className='GraphicalView-WidgetMenu-cardContent'>
                            <p className='GraphicalView-WidgetMenu-cardLabel'>
                              {draggedWidget.label}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </DragOverlay>
              </DndContext>
            </div>
          </Box>
        }
        FooterActionBarComp={
          <FooterActionBar size='large'>
            <div className='GraphicalView-WidgetMenu-footerSpacer' />
            <div className='GraphicalView-WidgetMenu-footerWrapper'>
              {isWidgetOverflow && (
                <InlineMessage
                  text={l("You've reached the max number")}
                  colorTheme='info'
                  size='medium'
                  leftIcon={<Icon kind='AlertInfoFilled' />}
                  className='GraphicalView-WidgetMenu-footerInlineMessage'
                />
              )}
              <Button
                type='button'
                kind='ghost'
                size='medium'
                onClick={handleCancel}>
                {l('Cancel')}
              </Button>
              <Button
                type='button'
                kind='filled'
                size='medium'
                onClick={handleSave}>
                {l('Save')}
              </Button>
            </div>
          </FooterActionBar>
        }
      />
    </Box>
  );
}

export default memo(GraphicalViewWidgetMenu);
